import React from 'react'
import { useResponsiveDropdown } from 'components/ResponsiveDropdown'
import { useVersioning } from 'modules/versioning'
import { Tooltip } from '@sketch/components'

import NotificationStatusButton from '../NotificationStatusButton'
import NotificationStatusDropdown from '../NotificationPartialStatusDropdown'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { NotificationSubscriptionStatus } from '@sketch/gql-types/expansive'

import useDocumentNotificationSubscription from '../../operations/useNotificationSubscription'

interface ShareSubscriptionStatusProps {
  shareIdentifier: string
}

const tooltipCopy: {
  [key in NotificationSubscriptionStatus]: JSX.Element
} = {
  ON: <>Stop getting notified on new comments</>,
  OFF: <>Get notified on new comments</>,
  PARTIAL: <>You’re being notified on some Artboards</>,
} as const

const ShareSubscriptionStatus: React.FC<ShareSubscriptionStatusProps> = props => {
  const { shareIdentifier } = props
  const updateNotificationStatus = useDocumentNotificationSubscription()
  const { share } = useVersioning()

  const notificationStatus = share.subscriptionStatus

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: NotificationStatusDropdown,
    dropdownProps: {
      type: 'share',
      notificationStatus,
      onNotificationChange: state =>
        updateNotificationStatus({ shareIdentifier, state }),
    },
  })

  const getButton = () => {
    if (notificationStatus === 'PARTIAL') {
      return (
        <>
          <NotificationStatusButton
            data-testid="document-notification-status"
            notificationStatus={notificationStatus}
            {...buttonProps}
          />
          {content}
        </>
      )
    } else {
      const newStatus = notificationStatus === 'ON' ? 'OFF' : 'ON'
      return (
        <NotificationStatusButton
          data-testid="document-notification-status"
          notificationStatus={notificationStatus}
          onClick={() => {
            updateNotificationStatus({
              shareIdentifier,
              state: newStatus,
            })
          }}
        />
      )
    }
  }

  return (
    <Tooltip
      placement="bottom-start"
      content={tooltipCopy[notificationStatus]}
      disabled={buttonProps['aria-expanded'] === true}
    >
      {getButton()}
    </Tooltip>
  )
}

export default ShareSubscriptionStatus
